import React, { useEffect, useState } from 'react';
import { Link, useLocation } from "react-router-dom";
import { XIcon, MenuIcon } from '@heroicons/react/outline';

const links = [
    {
        name: 'Home',
        href: '/'
    },
    {
        name: 'About us',
        href: '/about/'
    },
    {
        name: 'Practices',
        href: '/practices/'
    },
    {
        name: 'Careers',
        href: '/careers/'
    },
    {
        name: 'Feedback',
        href: '/feedback/',
    },
    {
        name: 'Contact us',
        href: '/contact/'
    },
];

export default function NavLinks({ onMobileMenuChange }) {
    const location = useLocation();
    const { pathname } = location;

    const [hovered, setHovered] = useState("");
    const [clickedMenu, setClickedMenu] = useState(false);

    useEffect(() => {
        document.body.style.overflow = clickedMenu ? "hidden" : "auto";
        onMobileMenuChange?.(clickedMenu);

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [clickedMenu, onMobileMenuChange]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return (
        <div>

            {/* ================= DESKTOP ================= */}

            <div className="hidden lg:flex items-center gap-2">

                {links.map((link) => {
                    const active = pathname === link.href;

                    return (
                        <Link
                            key={link.name}
                            to={link.href}
                            onMouseEnter={() => setHovered(link.name)}
                            onMouseLeave={() => setHovered("")}
                            className={`relative transition-all duration-300 ${
                                active
                                    ? "rounded-xl bg-gradient-to-br from-zinc-700 via-zinc-300 to-zinc-700 p-[1px]"
                                    : ""
                            }`}
                        >
                            <div
                                className={`
                                    relative
                                    overflow-hidden
                                    rounded-[11px]
                                    px-5
                                    py-2.5
                                    transition-all
                                    duration-300
                                    ${
                                        active
                                            ? "bg-gradient-to-b from-[#181818] via-[#101010] to-black"
                                            : ""
                                    }
                                `}
                            >
                                {/* Hover metallic border */}
                                {!active && (
                                    <div
                                        className={`
                                            absolute
                                            inset-0
                                            rounded-[11px]
                                            border
                                            transition-all
                                            duration-300
                                            ${
                                                hovered === link.name
                                                    ? "border-zinc-500"
                                                    : "border-transparent"
                                            }
                                        `}
                                    />
                                )}

                                {/* Metallic shine */}
                                {(hovered === link.name || active) && (
                                    <div className="absolute inset-0 bg-[linear-gradient(120deg,transparent_15%,rgba(255,255,255,.06)_50%,transparent_85%)] pointer-events-none" />
                                )}

                                <span
                                    className={`
                                        relative
                                        z-10
                                        transition-colors
                                        duration-300
                                        ${
                                            active
                                                ? "text-white"
                                                : hovered === link.name
                                                ? "text-white"
                                                : "text-zinc-300"
                                        }
                                    `}
                                >
                                    {link.name}
                                </span>
                            </div>
                        </Link>
                    );
                })}

                {/* Login Button */}

                <a
                    href="https://client.quilatonlawoffice.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
                        ml-3
                        rounded-xl
                        bg-gradient-to-br
                        from-zinc-700
                        via-zinc-300
                        to-zinc-700
                        p-[1px]
                        transition-all
                        duration-300
                        hover:scale-105
                        hover:shadow-[0_0_20px_rgba(255,255,255,0.15)]
                    "
                >
                    <div className="relative overflow-hidden rounded-[11px] bg-gradient-to-b from-[#181818] via-[#101010] to-black px-6 py-2.5">

                        <div className="absolute inset-0 bg-[linear-gradient(120deg,transparent_15%,rgba(255,255,255,.08)_50%,transparent_85%)] pointer-events-none" />

                        <span className="relative text-white tracking-[0.15em]">
                            LOGIN
                        </span>

                    </div>
                </a>

            </div>

            {/* ================= MOBILE ================= */}

            <div className="text-white flex lg:hidden">

                {clickedMenu ? (

                    <div className="mobile-menu-enter fixed inset-0 z-[100] flex min-h-[100dvh] flex-col items-center justify-center overflow-y-auto bg-[#090909] px-6 shadow-2xl shadow-black">

                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(255,255,255,.12),transparent_33rem)]" />
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-zinc-300 to-transparent" />

                        <XIcon
                            onClick={() => setClickedMenu(false)}
                            className="absolute right-5 top-5 z-10 h-10 w-10 cursor-pointer rounded-full border border-zinc-700 p-2 text-white transition hover:border-zinc-300 hover:bg-zinc-800"
                        />

                        <div className="relative z-10 space-y-4 text-center">

                            {links.map((link, index) => (
                                <Link
                                    key={link.name}
                                    to={link.href}
                                    onClick={() => setClickedMenu(false)}
                                    className={`mobile-menu-link block rounded-xl px-8 py-3 font-fontMontserrat text-3xl transition-colors duration-300 ${
                                        pathname === link.href
                                            ? "bg-zinc-800/80 text-white"
                                            : "text-zinc-400 hover:text-white"
                                    }`}
                                    style={{ animationDelay: `${120 + index * 65}ms` }}
                                >
                                    {link.name}
                                </Link>
                            ))}

                            <a
                                href="https://client.quilatonlawoffice.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mobile-menu-link inline-block mt-8 rounded-xl bg-gradient-to-br from-zinc-700 via-zinc-300 to-zinc-700 p-[1px]"
                                style={{ animationDelay: '530ms' }}
                            >
                                <div className="rounded-[11px] bg-gradient-to-b from-[#181818] via-[#101010] to-black px-8 py-3 text-white tracking-[0.15em]">
                                    LOGIN
                                </div>
                            </a>

                        </div>

                    </div>

                ) : (

                    <MenuIcon
                        onClick={() => setClickedMenu(true)}
                        className="h-10 w-10 cursor-pointer text-white"
                    />

                )}

            </div>

        </div>
    );
}
